import { CheckCircle2, XCircle, Loader2, Package } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface BatchVariant {
  index: number;
  name: string;
  status: 'pending' | 'success' | 'failed';
  quality?: number;
  size?: number;
  previewUrl?: string;
  error?: string;
}

interface BatchVariantGridProps {
  variants: BatchVariant[];
  done: number;
  total: number;
  finished?: boolean;
  totalTime?: number;
  onOpen?: (previewUrl: string) => void;
}

function fmtDuration(ms = 0): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}分${s % 60}秒` : `${s}秒`;
}

/** 批量生成进度网格：pending 转圈 / success 缩略图 / failed 红叉。 */
export function BatchVariantGrid({ variants, done, total, finished, totalTime, onOpen }: BatchVariantGridProps): JSX.Element {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const success = variants.filter((v) => v.status === 'success').length;

  return (
    <div className="glass-card p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-foreground">
        <Package size={13} className="text-neon-purple" />
        {finished ? `批量生成完成 (${success}/${total} 成功)` : `批量生成中 (${done}/${total})`}
      </div>

      <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
        {variants.map((v) => (
          <button
            key={`${v.index}-${v.name}`}
            onClick={() => v.previewUrl && onOpen?.(v.previewUrl)}
            disabled={!v.previewUrl}
            className="group relative aspect-square overflow-hidden rounded-lg border border-border/40 bg-muted/40"
            title={v.error || v.name}
          >
            {v.status === 'success' && v.previewUrl ? (
              <img src={v.previewUrl} alt={v.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-1 p-1 text-center">
                {v.status === 'pending' && <Loader2 size={16} className="animate-spin text-neon-cyan" />}
                {v.status === 'success' && <CheckCircle2 size={16} className="text-neon-green" />}
                {v.status === 'failed' && <XCircle size={16} className="text-red-400" />}
                <span className="text-[9px] text-muted-foreground">
                  {v.status === 'failed' ? '失败' : v.quality != null ? `${v.quality}分` : v.name}
                </span>
              </div>
            )}
            {v.status === 'success' && (
              <span className="absolute bottom-0.5 right-0.5 rounded-full bg-neon-green/90 p-0.5"><CheckCircle2 size={9} className="text-white" /></span>
            )}
          </button>
        ))}
      </div>

      {!finished ? (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className={cn('progress-neon h-full rounded-full transition-all')} style={{ width: `${pct}%` }} />
        </div>
      ) : (
        <div className="mt-2 text-[11px] text-muted-foreground">耗时 {fmtDuration(totalTime)}</div>
      )}
    </div>
  );
}
